import { TConfirmOpts, useConfirmStore } from '@/stores/feedback';

/**
 * 一条待结算记录。
 *
 * `resolve` 与调用方自带的 `onConfirm` 都收在这里，使「弹出 → 结算」的配对
 * 只有一份实现：应用层的 `showConfirm` 与内核经 `FEEDBACK_CONFIRM` 事件
 * 送来的请求共用同一条路径。
 */
interface IPendingConfirm {
  resolve: (confirmed: boolean) => void;
  onConfirm?: (confirmed: boolean) => void;
}

const pending = new Map<string, IPendingConfirm>();

/** 单调递增，不复用——复用会让迟到的结算命中后来的确认框 */
let idSeed = 0;

/**
 * 弹出确认框，返回用户的选择。
 *
 * 已有确认框未关闭时排在其后，不覆盖它。
 */
export function requestConfirm(options: TConfirmOpts): Promise<boolean> {
  const { onConfirm, ...entry } = options;
  idSeed += 1;
  const id = `confirm-${idSeed}`;

  const result = new Promise<boolean>((resolve) => {
    pending.set(id, { resolve, onConfirm });
  });

  useConfirmStore.getState().enqueueConfirm({ ...entry, id });

  return result;
}

/**
 * 结算指定确认框：出队、resolve、回调一次完成。
 *
 * 对已结算或不存在的 id 是幂等的空操作。
 */
export function settleConfirm(id: string, confirmed: boolean) {
  const record = pending.get(id);

  if (!record) {
    return;
  }

  pending.delete(id);
  useConfirmStore.getState().dequeueConfirm(id);
  record.resolve(confirmed);
  record.onConfirm?.(confirmed);
}

/**
 * 供 `ConfirmRenderer` 订阅内核事件用。
 *
 * 内核的 `confirm()` 自带 Promise，这里不需要返回值；写成模块级函数
 * 是为了引用恒定，不让 `useEditorEvent` 的依赖反复失效。
 */
export function handleEditorConfirm(options: TConfirmOpts): void {
  void requestConfirm(options);
}
